import type { Organization } from '@studio/models/organization';

// Per rule: ui-architecture (ai.knowledge/global) — org identity stays on semantic theme tokens.
const ORG_GRADIENT_CLASSES = [
  'bg-gradient-to-br from-primary to-focus',
  'bg-gradient-to-br from-focus to-primary-highlight',
  'bg-gradient-to-br from-primary-highlight to-success',
  'bg-gradient-to-br from-warning to-error',
  'bg-gradient-to-br from-success to-success-highlight'
] as const;

// FNV-1a keeps an organization's color stable across renders and sessions.
const stableHash = (value: string): number => {
  let hash = 0x811c9dc5;

  for (const character of value) {
    hash ^= character.charCodeAt(0);
    hash = Math.imul(hash, 0x01000193);
  }

  return hash >>> 0;
};

/** Stable key used for grouping, STag identity, and palette selection. */
export const organizationKey = (
  organization: Organization | undefined,
  fallbackId?: string | undefined
): string => {
  const id = String(organization?.id ?? fallbackId ?? '').trim();
  const name = organization?.name?.trim() ?? '';

  return id || name || 'studios';
};

/** Human-readable fallback for pre-bootstrap linking descriptors without org data. */
export const organizationLabel = (organization: Organization | undefined): string =>
  organization?.name?.trim() || 'Studios';

/** Theme-aware gradient assigned deterministically from the organization key. */
export const organizationGradientClassName = (
  organization: Organization | undefined,
  fallbackId?: Organization['id'] | undefined
): string => {
  const key = organizationKey(organization, fallbackId);

  if (key === 'studios') {
    return ORG_GRADIENT_CLASSES[0];
  }

  return ORG_GRADIENT_CLASSES[stableHash(key) % ORG_GRADIENT_CLASSES.length]!;
};
